import { CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * Tier-based verification badge system for DMV Soundstage
 * 
 * Badge colors by tier:
 * - Starter ($5): Gray
 * - Growth ($10): Blue
 * - Pro ($20): Purple
 * - Elite ($35): Gold
 * - Executive ($50): Red
 * - Label/Enterprise ($100): Black
 * 
 * Additional badges:
 * - Verified DMV: Manual admin approval
 * - Founder: Platform owner special badge
 */

const TIER_CONFIG = {
  starter: {
    color: '#6B7280', // Gray
    bgColor: 'rgba(107, 114, 128, 0.15)',
    borderColor: 'rgba(107, 114, 128, 0.3)',
    label: 'Starter',
    tooltip: 'Starter Artist — Active Subscription'
  },
  growth: {
    color: '#3B82F6', // Blue
    bgColor: 'rgba(59, 130, 246, 0.15)',
    borderColor: 'rgba(59, 130, 246, 0.3)',
    label: 'Growth',
    tooltip: 'Growth Artist — Active Subscription'
  },
  pro: {
    color: '#8B5CF6', // Purple
    bgColor: 'rgba(139, 92, 246, 0.15)',
    borderColor: 'rgba(139, 92, 246, 0.3)',
    label: 'Pro',
    tooltip: 'Pro Artist — Active Subscription'
  },
  elite: {
    color: '#F59E0B', // Gold
    bgColor: 'rgba(245, 158, 11, 0.15)',
    borderColor: 'rgba(245, 158, 11, 0.3)',
    label: 'Elite',
    tooltip: 'Elite Artist — Active Subscription'
  },
  executive: {
    color: '#EF4444', // Red
    bgColor: 'rgba(239, 68, 68, 0.15)',
    borderColor: 'rgba(239, 68, 68, 0.3)',
    label: 'Executive',
    tooltip: 'Executive Artist — Active Subscription'
  },
  label: {
    color: '#111827', // Black
    bgColor: 'rgba(17, 24, 39, 0.9)',
    borderColor: 'rgba(17, 24, 39, 1)',
    textColor: '#FFFFFF',
    label: 'Enterprise',
    tooltip: 'Enterprise Artist — Active Subscription'
  },
  enterprise: {
    color: '#111827', // Black
    bgColor: 'rgba(17, 24, 39, 0.9)',
    borderColor: 'rgba(17, 24, 39, 1)',
    textColor: '#FFFFFF',
    label: 'Enterprise',
    tooltip: 'Enterprise Artist — Active Subscription'
  }
};

// FOUNDER_CONFIG removed - founders now appear as regular tier-based artists publicly

const VERIFIED_DMV_CONFIG = {
  color: '#10B981', // Green
  bgColor: 'rgba(16, 185, 129, 0.15)',
  borderColor: 'rgba(16, 185, 129, 0.3)',
  label: 'Verified DMV',
  tooltip: 'Verified DMV — Approved Local Artist'
};

/**
 * TierBadge Component
 * 
 * @param {string} tier - Artist subscription tier (starter, growth, pro, elite, executive, label)
 * @param {string} subscriptionStatus - active, trial, past_due, canceled, null
 * @param {boolean} isFounder - Is platform owner/founder (INTERNAL ONLY - not shown publicly)
 * @param {boolean} isVerifiedDMV - Admin-approved verified DMV artist
 * @param {string} size - sm, md, lg
 * @param {boolean} showLabel - Show tier label text
 * @param {boolean} showTooltip - Enable tooltip on hover
 */
export function TierBadge({ 
  tier, 
  subscriptionStatus, 
  isFounder = false, 
  isVerifiedDMV = false,
  size = 'md',
  showLabel = false,
  showTooltip = true,
  className = ''
}) {
  // NOTE: Founder badge is NO LONGER shown publicly
  // Platform owners appear as regular tier-based artists
  // isFounder is kept for internal permission checks only

  // Check if subscription is active (active or trial)
  const isActive = subscriptionStatus === 'active' || subscriptionStatus === 'trial';
  
  // No badge if no tier or inactive subscription
  if (!tier || !isActive) {
    // Show inactive badge if past_due
    if (subscriptionStatus === 'past_due' && tier) {
      return (
        <InactiveBadge 
          tier={tier}
          size={size}
          showTooltip={showTooltip}
          className={className}
        />
      );
    }
    return null;
  }

  const config = TIER_CONFIG[tier.toLowerCase()];
  if (!config) return null;

  return (
    <BadgeDisplay 
      config={config}
      size={size}
      showLabel={showLabel}
      showTooltip={showTooltip}
      className={className}
    />
  );
}

/**
 * Verified DMV Badge (separate from tier)
 */
export function VerifiedDMVBadge({ size = 'md', showLabel = false, showTooltip = true, className = '' }) {
  return (
    <BadgeDisplay 
      config={VERIFIED_DMV_CONFIG}
      size={size}
      showLabel={showLabel}
      showTooltip={showTooltip}
      className={className}
    />
  );
}

/**
 * Badge Display Component
 */
function BadgeDisplay({ config, size, showLabel, showTooltip, className }) {
  const sizeClasses = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-xs',
    lg: 'text-sm'
  };

  const paddingSizes = {
    sm: 'px-1.5 py-0.5',
    md: 'px-2 py-1',
    lg: 'px-2.5 py-1.5'
  };

  const iconSize = sizeClasses[size] || sizeClasses.md;
  const textSize = textSizes[size] || textSizes.md;
  const padding = paddingSizes[size] || paddingSizes.md;

  const badgeContent = (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`inline-flex items-center gap-1 ${padding} rounded-full ${className}`}
      style={{
        background: config.bgColor,
        border: `1px solid ${config.borderColor}`,
        color: config.textColor || config.color
      }}
      title={showTooltip ? config.tooltip : undefined}
    >
      <CheckCircle 
        className={iconSize} 
        style={{ color: config.color }}
        fill={config.color}
        strokeWidth={0}
      />
      {showLabel && (
        <span className={`font-medium ${textSize}`} style={{ color: config.textColor || config.color }}>
          {config.label}
        </span>
      )}
    </motion.div>
  );

  return badgeContent;
}

/**
 * Inactive/Past Due Badge (grayed out)
 */
function InactiveBadge({ tier, size, showTooltip, className }) {
  const sizeClasses = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  const iconSize = sizeClasses[size] || sizeClasses.md;

  return (
    <div
      className={`inline-flex items-center p-1 rounded-full opacity-40 ${className}`}
      style={{
        background: 'rgba(107, 114, 128, 0.1)',
        border: '1px dashed rgba(107, 114, 128, 0.3)'
      }}
      title={showTooltip ? 'Subscription inactive — Renew to restore badge' : undefined}
    >
      <CheckCircle 
        className={iconSize} 
        style={{ color: '#6B7280' }}
        strokeWidth={1.5}
      />
    </div>
  );
}

/**
 * Tier Legend Component for Charts page
 */
export function TierLegend({ className = '' }) {
  return (
    <div className={`bg-card/50 backdrop-blur border border-border rounded-lg p-4 ${className}`}>
      <h3 className="font-heading text-sm font-bold uppercase mb-3 text-muted-foreground">
        Artist Tier Badges
      </h3>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {Object.entries(TIER_CONFIG).filter(([key]) => key !== 'enterprise').map(([tier, config]) => (
          <div key={tier} className="flex items-center gap-2">
            <CheckCircle 
              className="w-4 h-4" 
              style={{ color: config.color }}
              fill={config.color}
              strokeWidth={0}
            />
            <span className="text-xs text-muted-foreground">{config.label}</span>
          </div>
        ))}
      </div>
      
      <p className="text-xs text-muted-foreground leading-relaxed">
        Tier badges show an artist's membership level on DMV Soundstage. 
        Charts are powered by real listener requests — not payments.
      </p>
    </div>
  );
}

/**
 * Helper to get tier color
 */
export function getTierColor(tier) {
  const config = TIER_CONFIG[tier?.toLowerCase()];
  return config?.color || '#6B7280';
}

/**
 * Helper to check if artist should show badge
 * NOTE: Founder status no longer affects public badge display
 */
export function shouldShowBadge(tier, subscriptionStatus, isFounder = false) {
  // isFounder kept for internal permission checks but doesn't affect badge display
  if (!tier) return false;
  return subscriptionStatus === 'active' || subscriptionStatus === 'trial';
}

export default TierBadge;
